/**
 * Banner visual da notificação de conquista (presentational) — "O Desvelar".
 *
 * Encena o desbloqueio como uma cerimônia de premiação: raios de luz
 * irrompem girando atrás da medalha, um anel de flash dispara na
 * revelação, o metal do tier reveste a borda e o nome é "gravado" numa
 * serifada (Fraunces). O card inteiro é clicável (leva às conquistas do
 * perfil); o X fecha sem propagar. Toda a atmosfera está em trophy.css.
 */

import { type KeyboardEvent, type MouseEvent } from 'react'
import { motion, useReducedMotion } from 'framer-motion'
import { Sparkles, X } from 'lucide-react'
import { BadgeMedallion } from '@/components/super-admin/badges/BadgeMedallion'
import type { Tier } from '@/components/super-admin/badges/types'
import type { UserBadge } from '@/services/badges.service'
import { cn } from '@/lib/utils'
import './trophy.css'

const TIER_LABEL: Record<Tier, string> = {
  bronze: 'Bronze',
  prata: 'Prata',
  ouro: 'Ouro',
  platina: 'Platina',
}

interface TrophyBannerProps {
  userBadge: UserBadge
  onClick: () => void
  onClose: () => void
}

export function TrophyBanner({ userBadge, onClick, onClose }: TrophyBannerProps) {
  const reduceMotion = useReducedMotion()
  const { badge } = userBadge

  function handleKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      onClick()
    }
  }

  // Fechar não pode borbulhar pro onClick do card (que navega).
  function handleClose(e: MouseEvent) {
    e.stopPropagation()
    onClose()
  }

  return (
    <div
      role="alert"
      aria-live="polite"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      data-tier={badge.tier}
      className={cn(
        'trophy-banner relative flex w-[360px] max-w-[calc(100vw-2.5rem)] cursor-pointer',
        'items-center gap-3.5 rounded-2xl py-4 pl-5 pr-10'
      )}
    >
      {/* Atmosfera */}
      <span className="trophy-banner__rail" aria-hidden />
      <span className="trophy-banner__hairline" aria-hidden />
      <span className="trophy-banner__aurora" aria-hidden />
      <span className="trophy-banner__grain" aria-hidden />
      <span className="trophy-banner__sheen" aria-hidden />

      {/* Palco da medalha — luz, flash e faíscas */}
      <div className="trophy-banner__stage">
        <span className="trophy-banner__rays" aria-hidden />
        <span className="trophy-banner__glow" aria-hidden />
        {!reduceMotion && (
          <motion.span
            aria-hidden
            className="trophy-banner__flash"
            initial={{ scale: 0.4, opacity: 0.85 }}
            animate={{ scale: 2.6, opacity: 0 }}
            transition={{ duration: 0.9, delay: 0.12, ease: 'easeOut' }}
          />
        )}
        <span className="trophy-banner__spark" data-i="1" aria-hidden />
        <span className="trophy-banner__spark" data-i="2" aria-hidden />
        <span className="trophy-banner__spark" data-i="3" aria-hidden />
        <motion.div
          className="trophy-banner__medal"
          initial={reduceMotion ? false : { scale: 0, rotate: -45 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ type: 'spring', stiffness: 340, damping: 17, delay: 0.14 }}
        >
          <BadgeMedallion
            tier={badge.tier}
            iconUrl={badge.iconUrl}
            inscription={badge.name}
            size={68}
          />
        </motion.div>
      </div>

      {/* Texto */}
      <div className="trophy-banner__text">
        <motion.p
          className="trophy-banner__eyebrow"
          initial={reduceMotion ? false : { opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.3 }}
        >
          <Sparkles className="h-3 w-3" />
          Conquista desbloqueada
        </motion.p>
        <motion.p
          className="trophy-banner__name"
          initial={reduceMotion ? false : { opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.28, duration: 0.34 }}
        >
          {badge.name}
        </motion.p>
        <motion.p
          className="trophy-banner__meta"
          initial={reduceMotion ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.3 }}
        >
          <span className="trophy-banner__tier">{TIER_LABEL[badge.tier]}</span>
          <span className="trophy-banner__dot" />
          <span>+{badge.points} pts · ver minhas conquistas</span>
        </motion.p>
      </div>

      {/* Fechar */}
      <button
        type="button"
        aria-label="Fechar notificação"
        onClick={handleClose}
        className="trophy-banner__close"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  )
}
